interface DebounceOptions {
  leading?: boolean
  trailing?: boolean
}

export interface DebouncedFunc<T extends (...args: unknown[]) => unknown> {
  (...args: Parameters<T>): void
  cancel(): void
  flush(): void
}

/**
 * Creates a debounced version of the provided function that delays invocation
 * until `wait` milliseconds have elapsed since the last call.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait = 0,
  options?: DebounceOptions
): DebouncedFunc<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null
  const leading = options?.leading ?? false
  const trailing = options?.trailing ?? true

  function invoke() {
    if (lastArgs) {
      const args = lastArgs
      lastArgs = null
      func(...args)
    }
  }

  const debounced = ((...args: Parameters<T>) => {
    lastArgs = args

    if (leading && timeoutId === null) {
      func(...args)
      lastArgs = null
    }

    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      timeoutId = null
      if (trailing && lastArgs !== null) {
        invoke()
      }
    }, wait)
  }) as DebouncedFunc<T>

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    lastArgs = null
  }

  debounced.flush = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
      invoke()
    }
  }

  return debounced
}
